/**
 * CASL Ability Definition
 *
 * This file contains the core ability builder that transforms backend permissions
 * into CASL rules for authorization checks throughout the application.
 */

import { AbilityBuilder, createMongoAbility, MongoAbility } from '@casl/ability'
import { Action, NormalizedPermission, Subject, UserPermission } from './types'

/**
 * Application ability type
 */
export type AppAbility = MongoAbility<[Action, Subject]>

/**
 * Normalize backend permissions into CASL format
 *
 * Transforms permissions from the backend API (with resource and action strings)
 * into normalized format with typed actions and subjects
 *
 * @param permissions - Array of user permissions from backend
 * @returns Array of normalized permissions
 */
export function normalizePermissions(permissions: UserPermission[]): NormalizedPermission[] {
    return permissions
        .map(permission => {
            // Map backend resource to frontend subject
            const subject = permission.resource
            // Map backend action to frontend action
            const action = permission.action

            // Skip unmapped permissions
            if (!subject || !action) {
                console.warn(`Unmapped permission: ${permission.resource}.${permission.action}`)
                return null
            }

            return {
                action: action as Action,
                subject: subject as Subject,
            }
        })
        .filter((p): p is NormalizedPermission => p !== null)
}

/**
 * Define CASL ability based on user permissions
 *
 * This is the main function that transforms backend permissions into CASL abilities.
 * It should be called whenever user permissions are loaded or updated.
 *
 * @example
 * ```typescript
 * const permissions = await api.permission.getMyPermissions()
 * const ability = defineAbilityFor(permissions.data)
 *
 * if (ability.can('create', 'User')) {
 *   // User can create users
 * }
 * ```
 *
 * @param permissions - Array of user permissions from backend
 * @returns CASL ability instance
 */
export function defineAbilityFor(permissions: UserPermission[] = []): AppAbility {
    const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility)

    // Normalize permissions
    const normalizedPermissions = normalizePermissions(permissions)

    // Define abilities based on normalized permissions
    normalizedPermissions.forEach(({ action, subject }) => {
        can(action, subject)
    })

    // Check if user has 'all' permission (super admin)
    const hasAllPermissions = permissions.some(
        p =>
            (p.resource.toLowerCase() === 'all' || p.resource === '*') &&
            (p.action.toLowerCase() === 'operate' || p.action === '*'),
    )

    // If user has all permissions, grant full access
    if (hasAllPermissions) {
        can('operate', 'all')
        can('view', 'all')
    }

    return build()
}

/**
 * Create a default ability with no permissions
 * Used as fallback when permissions haven't loaded yet
 */
export function createDefaultAbility(): AppAbility {
    return defineAbilityFor([])
}

export function createFullAccessAbility(): AppAbility {
    const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility)

    can('operate', 'all')
    can('view', 'all')

    return build()
}
